// src/components/EditDecisionPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { DecisionCreator } from './DecisionCreator';
import type { Decision } from '../types/decision';
import { User } from 'firebase/auth';

interface EditDecisionPageProps {
  user: User | null;
  onError?: (error: string) => void;
}

export function EditDecisionPage({ user, onError }: EditDecisionPageProps) {
  const { decisionId } = useParams<{ decisionId: string }>();
  const navigate = useNavigate();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadDecision = async () => {
      if (!decisionId) return;

      try {
        const decisionRef = doc(db, 'decisions', decisionId);
        const decisionSnap = await getDoc(decisionRef);

        if (!decisionSnap.exists()) {
          throw new Error('Decision not found');
        }

        const decisionData = decisionSnap.data();
        
        // Check if user is the creator
        if (user?.uid !== decisionData.createdBy) {
          throw new Error('You do not have permission to edit this decision');
        }
        
        // Check if there are already participants
        if (decisionData.participants > 0) {
          throw new Error('Cannot edit a decision after participants have joined');
        }

        setDecision({
          id: decisionSnap.id,
          ...decisionData
        } as Decision);
      } catch (err) {
        console.error('Error loading decision:', err);
        setError(err instanceof Error ? err.message : 'Failed to load decision');
        if (onError) {
          onError(err instanceof Error ? err.message : 'Failed to load decision');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDecision();
  }, [decisionId, user, onError]);

  const handleUpdate = async (data: { question: string; details?: string; visibility?: 'public' | 'unlisted' }) => {
    if (!decision) return;
    setError(null);
    setIsUpdating(true);

    try {
      const decisionRef = doc(db, 'decisions', decision.id);
      await updateDoc(decisionRef, {
        question: data.question,
        details: data.details || null,
        visibility: data.visibility || 'unlisted',
        lastUpdated: Timestamp.now()
      });

      navigate(`/d/${decisionId}`);
    } catch (err) {
      console.error('Error updating decision:', err);
      setError(err instanceof Error ? err.message : 'Failed to update decision');
      if (onError) {
        onError(err instanceof Error ? err.message : 'Failed to update decision');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-3xl px-4">
        <Card>
          <CardContent className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !decision) {
    return (
      <div className="container mx-auto max-w-3xl px-4">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Decision not found'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-4">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DecisionCreator 
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
        user={user!}
        onError={onError}
        initialData={{
          question: decision.question,
          details: decision.details,
          visibility: decision.visibility || 'unlisted'
        }}
        isEditing={true}
      />
    </div>
  );
}